import { getContrastColor } from '@/lib/colorUtils';
import { useState, useEffect, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";

interface FloorplanTourPreviewProps {
  northId: string;
  floorplanData?: any;
  tourData?: any;
  forceMobileLayout?: boolean;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  propertyImage?: string;
  isDemo?: boolean;
}

export const FloorplanTourPreview = ({
  northId,
  floorplanData,
  tourData,
  forceMobileLayout = false,
  primaryColor,
  secondaryColor,
  propertyImage,
  isDemo = false,
}: FloorplanTourPreviewProps) => {
  const [isTourModalOpen, setIsTourModalOpen] = useState(false);
  const [currentPlanIndex, setCurrentPlanIndex] = useState(0);

  // Function to lighten a hex color
  const lightenColor = (hex: string | null, percent: number = 40) => {
    // Return a default light color if hex is null or undefined
    if (!hex) return '#f0f0f0';

    // Remove # if present
    hex = hex.replace('#', '');

    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Lighten by mixing with white
    const lightenAmount = percent / 100;
    const newR = Math.round(r + (255 - r) * lightenAmount);
    const newG = Math.round(g + (255 - g) * lightenAmount);
    const newB = Math.round(b + (255 - b) * lightenAmount);

    // Convert back to hex
    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  };

  const lightSecondaryColor = lightenColor(secondaryColor, 85);
  // Handle both normalized structure and raw array from API
  const floorPlans = Array.isArray(floorplanData) 
    ? floorplanData 
    : floorplanData?.floorPlans || [];
  
  const hasFloorplan = floorPlans.length > 0;
  const hasTour = tourData && (tourData.tourUrl || tourData.viewerUrl || tourData.embedUrl);

  // If neither exists, don't render anything
  if (!hasFloorplan && !hasTour) {
    return null;
  }

  const gridClass = hasFloorplan && hasTour
    ? (forceMobileLayout ? "grid grid-cols-1 gap-6 items-start" : "grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6 items-start")
    : "grid grid-cols-1 gap-6";

  return (
    <div className="mb-12 p-8 rounded-xl border border-border/50 shadow-sm bg-muted/5">
      <div>
        <h3 className="text-sm font-semibold tracking-wider text-foreground mb-6">EXPLORE THE SPACE</h3>
        <p className="text-muted-foreground leading-relaxed text-base mb-8">
          Get an immersive view of the property with our interactive 3D tour and detailed floor plans
        </p>
      </div>

      <div className={gridClass}>
        {/* 3D Tour Section */}
        {hasTour && (
          <div className="space-y-4">
            <h4 className="text-sm font-semibold tracking-wider text-foreground text-center">
              3D VIRTUAL TOUR
            </h4>
            
            {tourData.embedUrl ? (
              <div className="relative w-full" style={{ paddingBottom: '75%' }}>
                <iframe
                  src={tourData.embedUrl}
                  className="absolute top-0 left-0 w-full h-full rounded-lg"
                  frameBorder="0"
                  allowFullScreen
                  title="3D Virtual Tour"
                />
              </div>
            ) : (tourData.tourUrl || tourData.viewerUrl) ? (
              <>
                <div 
                  className="relative flex flex-col items-center justify-center p-8 rounded-lg min-h-[400px] overflow-hidden"
                  style={{
                    backgroundImage: propertyImage ? `url(${propertyImage})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  {propertyImage && (
                    <div className="absolute inset-0 bg-black/60" />
                  )}
                  <div className="relative z-10 flex flex-col items-center justify-center text-center">
                    <svg
                      className="w-16 h-16 mb-4 text-white drop-shadow-lg"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                    <p className="text-white mb-6 drop-shadow-lg font-medium text-lg">
                      Experience a full 3D walkthrough of your home
                    </p>
                    {isDemo ? (
                      <div className="inline-flex items-center px-4 py-2 bg-muted text-muted-foreground rounded-md cursor-not-allowed opacity-60">
                        Launch Virtual Tour
                        <svg
                          className="w-4 h-4 ml-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      </div>
                    ) : (
                      <button
                        onClick={() => setIsTourModalOpen(true)}
                        className="inline-flex items-center px-4 py-2 rounded-md font-medium transition-opacity hover:opacity-80 hover-scale"
                        style={{
                          backgroundColor: primaryColor || 'hsl(var(--primary))',
                          color: primaryColor ? getContrastColor(primaryColor) : 'white'
                        }}
                      >
                        Launch Virtual Tour
                        <svg
                          className="w-4 h-4 ml-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                <Dialog open={isTourModalOpen} onOpenChange={setIsTourModalOpen}>
                  <DialogContent className="max-w-7xl h-[95vh] p-0 gap-0 flex flex-col">
                    <DialogHeader className="px-6 py-4 border-b shrink-0">
                      <DialogTitle>3D Virtual Tour</DialogTitle>
                    </DialogHeader>
                    <div className="relative w-full flex-1 min-h-0 overflow-hidden">
                      <iframe
                        src={tourData.tourUrl || tourData.viewerUrl}
                        className="w-full h-full"
                        frameBorder="0"
                        allowFullScreen
                        title="3D Virtual Tour"
                      />
                    </div>
                  </DialogContent>
                </Dialog>
              </>
            ) : null}
          </div>
        )}

        {/* Floorplan Section */}
        {hasFloorplan && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold tracking-wider text-foreground">
                FLOOR PLANS
              </h4>
              {floorPlans.length > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPlanIndex(Math.max(0, currentPlanIndex - 1))}
                    disabled={currentPlanIndex === 0}
                    className="h-8 w-8 rounded-full border border-border bg-background hover:bg-muted flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: primaryColor ? `${primaryColor}20` : undefined,
                      borderColor: primaryColor || undefined
                    }}
                    aria-label="Previous floor plan"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                  </button>
                  <span className="text-xs text-muted-foreground">
                    {currentPlanIndex + 1} / {floorPlans.length}
                  </span>
                  <button
                    onClick={() => setCurrentPlanIndex(Math.min(floorPlans.length - 1, currentPlanIndex + 1))}
                    disabled={currentPlanIndex === floorPlans.length - 1}
                    className="h-8 w-8 rounded-full border border-border bg-background hover:bg-muted flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: primaryColor ? `${primaryColor}20` : undefined,
                      borderColor: primaryColor || undefined
                    }}
                    aria-label="Next floor plan"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </button>
                </div>
              )}
            </div>

            <div className="w-full">
              {floorPlans.map((plan: any, index: number) => (
                <div
                  key={index}
                  className={`space-y-3 transition-opacity duration-300 ${
                    index === currentPlanIndex ? 'block' : 'hidden'
                  }`}
                >
                  {(plan.description || plan.name) && (
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-foreground">
                        {plan.description || plan.name}
                      </p>
                      {plan.sqft && (
                        <span className="text-sm text-muted-foreground">
                          {Math.round(plan.sqft).toLocaleString()} sq ft
                        </span>
                      )}
                    </div>
                  )}

                  {(plan.url || plan.img) && (
                    <div className="w-full overflow-hidden rounded-lg bg-muted/20">
                      <img
                        src={plan.url || plan.img}
                        alt={(plan.description || plan.name) || `Floor plan ${index + 1}`}
                        className="w-full h-auto object-contain"
                        style={{ maxHeight: '500px' }}
                        loading="lazy"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
